import Head from 'next/head';
import { Fragment } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled, { css } from 'styled-components';

import { replaceString } from 'imports/core/ui/helpers';
import { useResponsive } from '/imports/core/api/responsiveContext';
import intlHook from '/imports/core/api/useIntl';
import {
  getBrandName,
  getCompanyCountryAdjective,
  getCompanyNameByHost,
  getCompanyNumber,
  getCompanyRegistrationOffice,
  getContactEmail,
  getLandingAddress,
} from '/lib/helpers';

const CookiePolicy = () => {
  const { t } = intlHook();
  const { host } = useResponsive();
  const getContent = () => {
    return `
# ${t('cookie_title_1')}  
**${t('cookie_text_1')}**

${t('cookie_text_2')}

## 1. ${t('cookie_text_title_1')}  
${t('cookie_text_1_1')}

## 2. ${t('cookie_text_title_2')}  
${replaceString(t('cookie_text_2_1'), {
  COMPANY_NAME: getCompanyNameByHost(host),
  REGISTRATION_NUMBER: getCompanyNumber(host),
  COMPANY_ADDRESS: getLandingAddress(host),
  DOMAIN: getBrandName(host),
  COUNTRY_ADJECTIVE: getCompanyCountryAdjective(host),
  REGISTRATION_OFFICE: getCompanyRegistrationOffice(host),
})}

${t('cookie_text_2_2')}

## 3. ${t('cookie_text_title_3')} 
${t('cookie_text_3_1')}

${t('cookie_text_3_2_1')}

${t('cookie_text_title_3_1')} :

### 3.1. ${t('cookie_text_title_3')}  
${t('cookie_text_3_1_1')}

| ${t('table_head_1')}  | ${t('table_head_2')}                     | ${t('table_head_3')}                                  | ${t('table_head_4')}             |
|---------------------|-------------------------------|---------------------------------------------------------------|--------------------------------|
| **Data Dog**      | _dd_s                         | ${t('cookie_purpose_1')}                     | ${t('cookie_expiry_1')} |
| **Axeptio**         | axeptio_all_vendors           | ${t('cookie_purpose_2')}                                    | ${t('cookie_expiry_2')}                |
| **Axeptio**         | axeptio_authorized_vendors    | ${t('cookie_purpose_3')}           | ${t('cookie_expiry_2')}               |
| **Axeptio**         | axeptio_cookies               | ${t('cookie_purpose_4')} | ${t('cookie_expiry_2')} |

### 3.2. ${t('cookie_text_title_3_2')}  
${t('cookie_text_3_2')}

## 3.3. ${t('cookie_text_title_3_3')}

${t('cookie_text_3_3')}

| **${t('table_head_1')}**  | **${t('table_head_2')}**            | **${t('table_head_3')}**                                                                                     | **${t('table_head_4')}**          |
|-------------------------|-----------------------|---------------------------------------------------------------------------------------------------|---------------------------------|
| DoubleClick (Google)    | test_cookie          | ${t('cookie_purpose_5')}                                       | ${t('cookie_expiry_3')}                     |
| Meta                    | _fbp                 | ${t('cookie_purpose_6')} | ${t('cookie_expiry_4')}                         |
| AdSense (Google)        | _gcl_au              | ${t('cookie_purpose_6')}                                               | ${t('cookie_expiry_5')}                       |
| Microsoft               | _uetsid, _uetvid    | ${t('cookie_purpose_8')} | ${t('cookie_expiry_6')}             |
| Bing (Microsoft)        | MUID                 | ${t('cookie_purpose_6')}                                               | ${t('cookie_expiry_7')}                           |

## 3.4. ${t('cookie_text_title_3_4')}

${t('cookie_text_3_4')}

| **${t('table_head_1')}**  | **${t('table_head_2')}**            | **${t('table_head_3')}**                               | **${t('table_head_4')}**          |
|-------------------------|-----------------------|-------------------------------------------|---------------------------------|
| Indicative              | Indicative_*         | ${t('cookie_purpose_9')}                           | ${t('cookie_expiry_8')}                       |
| Indicative              | indicative_unique_id | ${t('cookie_purpose_9')}                            | ${t('cookie_expiry_9')}                        |
| Google Analytics (Google)              | _ga | ${t('cookie_purpose_10')}                            | ${t('cookie_expiry_10')}                  |
|Google Analytics (Google)            | _ga_* | ${t('cookie_purpose_10')}                           | ${t('cookie_expiry_7')}                       |
|Hotjar            | _hjSessionUser_* | ${t('cookie_purpose_11')}                           | ${t('cookie_expiry_7')}                      |
|Hotjar            | _hjSession_* | ${t('cookie_purpose_11')}                           | ${t('cookie_expiry_7')}                      |
|Sendinblue            | sib_cuid | ${t('cookie_purpose_12')}                        | ${t('cookie_expiry_11')}                     |


## 4. ${t('cookie_text_title_4')}

### 4.1  ${t('cookie_text_title_4_1')}  

${t('cookie_text_4_1')}

${t('cookie_text_4_2')}  

${t('cookie_text_4_3')}  

${t('cookie_text_4_4')} 

${t('cookie_text_4_5')}


### 4.2 ${t('cookie_text_title_4_2')}  

${t('cookie_text_4_2_1')}  

${t('cookie_text_4_2_2')}  
 
${t('cookie_text_4_2_3')}   

${t('cookie_text_4_2_4')}

${t('cookie_text_4_2_5')}

${t('cookie_text_4_2_6')}  

- [**Edge**](https://support.microsoft.com/fr-fr/windows/g%C3%A9rer-les-cookies-dans-microsoft-edge-afficher-autoriser-bloquer-supprimer-et-utiliser-168dab11-0753-043d-7c16-ede5947fc64d)  
- [**Google Chrome**](https://support.google.com/chrome/answer/95647?hl=fr&hlrm=en)   
- [**Opéra**](https://help.opera.com/en/latest/web-preferences/#cookies)   
- [**Safari**](https://support.apple.com/fr-fr/safari)   


##  5. ${t('cookie_text_title_5')} 

${t('cookie_text_5_1')}

## 6. ${t('cookie_text_title_6')}  

${replaceString(t('cookie_text_6_1'), {
  'contact@resumedone.co': getContactEmail(host),
})}

## 7. ${t('cookie_text_title_7')}  

${t('cookie_text_7_1')}

    `;
  };

  return (
    <Fragment>
      <Head>
        <meta name="robots" content="nofollow, noindex" />
      </Head>
      <StyledMarkDown children={getContent()} remarkPlugins={[remarkGfm]} />
    </Fragment>
  );
};

const StyledMarkDown = styled(ReactMarkdown)`
  padding: 50px;
  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      direction: rtl;
    `}

  h1 {
    text-align: center;
  }

  p {
    font-size: 20px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 10px;
    text-align: left;
  }

  th {
    background-color: #f4f4f4;
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
`;

export default CookiePolicy;
